import { useState } from 'react';
import { formaterDateHeure } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { Bloc, Tableau } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { Icone } from '../composants/Icone';

interface Anomalie {
  reference?: string;
  message: string;
}

interface ResultatControle {
  code: string;
  libelle: string;
  statut: 'OK' | 'ANOMALIE';
  nbAnomalies: number;
  anomalies: Anomalie[];
}

interface Rapport {
  date: string;
  controles: ResultatControle[];
}

export function Controle() {
  const { donnees, chargement, erreur, recharger } = useRessource<Rapport>('/controle-coherence');
  const [detaille, setDetaille] = useState<string | null>(null);

  const rapport = donnees;
  const anomalies = rapport ? rapport.controles.filter((c) => c.statut === 'ANOMALIE') : [];

  return (
    <>
      <p className="entete__fil">Contrôle</p>
      <div className="entete">
        <h1>Contrôle de cohérence</h1>
        {rapport && (
          <span className="entete__ref">exécuté le {formaterDateHeure(rapport.date)}</span>
        )}
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Contrôle impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {rapport &&
        (anomalies.length === 0 ? (
          <BandeauAlerte nature="information" titre="Aucune anomalie">
            Tous les invariants contrôlés sont respectés sur l’ensemble des données.
          </BandeauAlerte>
        ) : (
          <BandeauAlerte nature="gestion" titre="Anomalies détectées">
            {anomalies.length} contrôle(s) remontent des écarts. Détail par ligne ci-dessous.
          </BandeauAlerte>
        ))}

      <Bloc
        titre="Invariants (doc 02)"
        meta={chargement ? 'exécution…' : undefined}
        actions={
          <button
            type="button"
            className="bouton bouton--icone bouton--compact"
            onClick={() => void recharger()}
          >
            <Icone nom="controle" taille={15} />
            Relancer
          </button>
        }
      >
        <Tableau
          colonnes={[
            { cle: 'code', entete: 'Code', num: true, rendu: (c: ResultatControle) => c.code },
            { cle: 'libelle', entete: 'Contrôle', rendu: (c: ResultatControle) => c.libelle },
            {
              cle: 'statut',
              entete: 'Résultat',
              rendu: (c: ResultatControle) =>
                c.statut === 'OK' ? (
                  <Pastille ton="succes">Conforme</Pastille>
                ) : (
                  <Pastille ton="refus">{`${c.nbAnomalies} anomalie(s)`}</Pastille>
                ),
            },
            {
              cle: 'action',
              entete: '',
              num: true,
              rendu: (c: ResultatControle) =>
                c.nbAnomalies > 0 ? (
                  <button
                    type="button"
                    className="bouton bouton--fantome bouton--compact"
                    onClick={() => setDetaille(detaille === c.code ? null : c.code)}
                  >
                    {detaille === c.code ? 'Masquer' : 'Détail'}
                  </button>
                ) : (
                  '—'
                ),
            },
          ]}
          lignes={rapport?.controles ?? []}
          cle={(c) => c.code}
          vide="Aucun contrôle."
        />
      </Bloc>

      {rapport?.controles
        .filter((c) => c.code === detaille)
        .map((c) => (
          <Bloc key={c.code} titre={`Détail — ${c.libelle}`} meta={c.code}>
            <Tableau
              colonnes={[
                {
                  cle: 'ref',
                  entete: 'Objet',
                  num: true,
                  rendu: (a: Anomalie) => a.reference ?? '—',
                },
                { cle: 'msg', entete: 'Anomalie', rendu: (a: Anomalie) => a.message },
              ]}
              lignes={c.anomalies}
              cle={(a, i) => `${c.code}-${i}`}
            />
          </Bloc>
        ))}
    </>
  );
}
